import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import BenevolCheckLogo from '../components/BenevolCheckLogo';
import PrintableCertificate from '../components/PrintableCertificate';
import '../styles/print.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CertificateDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [sendingValidationEmail, setSendingValidationEmail] = useState(false);
  const [validationEmailSuccess, setValidationEmailSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [comment, setComment] = useState('');
  const [competencies, setCompetencies] = useState({});
  const printRef = useRef();

  // Définition des compétences
  const competencyCategories = [
    {
      title: 'Dans l\'action',
      items: [
        { key: 'learning_ability', label: 'Capacité d\'apprentissage', desc: 'il/elle a bien compris en quoi consistait sa mission pour en maîtriser la pratique' },
        { key: 'autonomy', label: 'Autonomie', desc: 'il/elle a exercé son activité sans avoir besoin d\'une supervision' },
        { key: 'adaptability', label: 'Adaptabilité', desc: 'il/elle a réagi avec pertinence pour modifier sa façon de faire face aux problèmes rencontrés' },
        { key: 'creativity', label: 'Créativité', desc: 'il/elle a proposé des idées pour rendre son activité plus efficace ou plus conviviale' }
      ]
    },
    {
      title: 'Dans l\'équipe',
      items: [
        { key: 'sociability', label: 'Sociabilité', desc: 'il/elle a facilement trouvé sa place parmi les autres membres du groupe' },
        { key: 'cooperation', label: 'Coopération', desc: 'dans son action, il/elle a tenu compte de l\'activité des autres membres de son équipe' },
        { key: 'empathy', label: 'Empathie', desc: 'dans les moments de tension, il/elle a su se mettre à la place de l\'autre pour comprendre son point de vue et éviter les conflits' },
        { key: 'leadership', label: 'Leadership', desc: 'il/elle a montré des capacités pour motiver l\'activité des autres et les solliciter en leur apportant si nécessaire un conseil ou un appui' }
      ]
    },
    {
      title: 'Dans l\'association',
      items: [
        { key: 'rigor_reliability', label: 'Rigueur et fiabilité', desc: 'il/elle a exercé son activité dans le respect des règles, de pratiques et des valeurs de l\'association' },
        { key: 'responsibility', label: 'Responsabilité', desc: 'il/elle s\'est senti(e) personnellement concerné(e) par la bonne réalisation des tâches ou la conduite des projets jusqu\'à leur accomplissement' },
        { key: 'curiosity', label: 'Curiosité', desc: 'il/elle s\'est intéressé(e) à la vie de l\'association, à son projet associatif et à ses diverses activités' },
        { key: 'engagement', label: 'Engagement', desc: 'il/elle s\'est montré(e) prêt(e) à prendre des responsabilités dans l\'animation, la vie collective ou le développement de l\'association' }
      ]
    }
  ];

  useEffect(() => {
    fetchCertificate();
  }, [id]);

  useEffect(() => {
    // Initialiser les compétences et le commentaire quand le certificat est chargé
    if (certificate) {
      setCompetencies(certificate.competencies || {});
      setComment(certificate.comment || '');
    }
  }, [certificate]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/${id}`, {
        headers: getAuthHeader()
      });
      setCertificate(response.data);
    } catch (error) {
      setError('Erreur lors du chargement de l\'attestation');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    if (!certificate) return;
    
    setDownloadingPdf(true);
    
    try {
      // Télécharger le PDF depuis le backend
      const response = await axios.get(
        `${API}/certificates/${id}/pdf`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Attestation_${certificate.volunteer_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur lors de la génération du PDF:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const copyValidationLink = () => {
    const link = `${window.location.origin}/validate/${certificate.validation_link}`;
    navigator.clipboard.writeText(link);
    alert('Lien de validation copié dans le presse-papier !');
  };

  const handleCompetencyChange = (key) => {
    setCompetencies(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleValidate = async () => {
    setValidating(true);
    setError('');
    
    try {
      // S'assurer que toutes les compétences ont une valeur booléenne
      const fullCompetencies = {
        learning_ability: competencies.learning_ability || false,
        autonomy: competencies.autonomy || false,
        adaptability: competencies.adaptability || false,
        creativity: competencies.creativity || false,
        sociability: competencies.sociability || false,
        cooperation: competencies.cooperation || false,
        empathy: competencies.empathy || false,
        leadership: competencies.leadership || false,
        rigor_reliability: competencies.rigor_reliability || false,
        responsibility: competencies.responsibility || false,
        curiosity: competencies.curiosity || false,
        engagement: competencies.engagement || false
      };
      
      // D'abord, mettre à jour les compétences et le commentaire
      await axios.put(
        `${API}/certificates/${id}`,
        { competencies: fullCompetencies, comment: comment || null },
        { headers: getAuthHeader() }
      );
      
      // Ensuite, valider le certificat
      await axios.post(
        `${API}/certificates/${id}/validate`,
        { validator_email: user?.email },
        { headers: getAuthHeader() }
      );
      
      setValidationSuccess(true);
      // Recharger le certificat pour voir le statut mis à jour
      await fetchCertificate();
    } catch (err) {
      console.error('Erreur lors de la validation:', err);
      // Extraire le message d'erreur de façon sécurisée
      let errorMessage = 'Erreur lors de la validation de l\'attestation';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(d => d.msg || d).join(', ');
        }
      }
      setError(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  const handleSendValidationEmail = async () => {
    setSendingValidationEmail(true);
    setValidationEmailSuccess(false);
    
    try {
      await axios.post(
        `${API}/certificates/${id}/send-validation-request`,
        {},
        { headers: getAuthHeader() }
      );
      
      setValidationEmailSuccess(true);
      setTimeout(() => setValidationEmailSuccess(false), 5000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setSendingValidationEmail(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    setEmailSuccess(false);
    
    try {
      await axios.post(
        `${API}/certificates/${id}/send-email`,
        {},
        { headers: getAuthHeader() }
      );
      
      setEmailSuccess(true);
      setTimeout(() => setEmailSuccess(false), 5000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Attestation introuvable'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const competenciesLabels = {
    learning_ability: { label: 'Capacité d\'apprentissage', desc: 'il/elle a bien compris en quoi consistait sa mission pour en maîtriser la pratique' },
    autonomy: { label: 'Autonomie', desc: 'il/elle a exercé son activité sans avoir besoin d\'une supervision' },
    adaptability: { label: 'Adaptabilité', desc: 'il/elle a réagi avec pertinence pour modifier sa façon de faire face aux problèmes rencontrés' },
    creativity: { label: 'Créativité', desc: 'il/elle a proposé des idées pour rendre son activité plus efficace ou plus conviviale' },
    sociability: { label: 'Sociabilité', desc: 'il/elle a facilement trouvé sa place parmi les autres membres du groupe' },
    cooperation: { label: 'Coopération', desc: 'dans son action, il/elle a tenu compte de l\'activité des autres membres de son équipe' },
    empathy: { label: 'Empathie', desc: 'dans les moments de tension, il/elle a su se mettre à la place de l\'autre pour comprendre son point de vue et éviter les conflits' },
    leadership: { label: 'Leadership', desc: 'il/elle a montré des capacités pour motiver l\'activité des autres et les solliciter en leur apportant si nécessaire un conseil ou un appui' },
    rigor_reliability: { label: 'Rigueur et fiabilité', desc: 'il/elle a exercé son activité dans le respect des règles, de pratiques et des valeurs de l\'association' },
    responsibility: { label: 'Responsabilité', desc: 'il/elle s\'est senti(e) personnellement concerné(e) par la bonne réalisation des tâches ou la conduite des projets jusqu\'à leur accomplissement' },
    curiosity: { label: 'Curiosité', desc: 'il/elle s\'est intéressé(e) à la vie de l\'association, à son projet associatif et à ses diverses activités' },
    engagement: { label: 'Engagement', desc: 'il/elle s\'est montré(e) prêt(e) à prendre des responsabilités dans l\'animation, la vie collective ou le développement de l\'association' }
  };

  const categorizedCompetencies = {
    action: ['learning_ability', 'autonomy', 'adaptability', 'creativity'],
    team: ['sociability', 'cooperation', 'empathy', 'leadership'],
    association: ['rigor_reliability', 'responsibility', 'curiosity', 'engagement']
  };

  const selectedCompetencies = Object.entries(certificate.competencies)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header - Hidden when printing */}
      <header className="bg-white shadow-sm print:hidden">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
            <BenevolCheckLogo size="md" />
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {certificate.status === 'pending_validation' && (
                <>
                  {certificate.signatory_email && (
                    <Button 
                      size="sm" 
                      onClick={handleSendValidationEmail} 
                      disabled={sendingValidationEmail}
                      data-testid="send-validation-email-button" 
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                    >
                      {sendingValidationEmail ? '📤 Envoi...' : '📧 Envoyer au responsable'}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={copyValidationLink} data-testid="copy-validation-link" className="w-full sm:w-auto text-xs sm:text-sm">
                    🔗 Lien validation
                  </Button>
                </>
              )}
              {certificate.status === 'validated' && (
                <>
                  <Button 
                    size="sm" 
                    onClick={handleSendEmail} 
                    disabled={sendingEmail}
                    data-testid="send-email-button" 
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                  >
                    {sendingEmail ? '📤 Envoi...' : '📧 Envoyer par email'}
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleDownloadPdf} 
                    disabled={downloadingPdf}
                    data-testid="download-pdf-button" 
                    className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
                  >
                    {downloadingPdf ? '⏳ Génération...' : '📄 Télécharger PDF'}
                  </Button>
                </>
              )}
              <Button size="sm" onClick={handlePrint} data-testid="print-button" className="w-full sm:w-auto">
                🖨️ Imprimer
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-button" className="w-full sm:w-auto">
                ← Retour
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 sm:py-8 max-w-4xl print:px-0 print:py-0">
        {/* Email Success Alert */}
        {emailSuccess && (
          <Alert className="mb-4 sm:mb-6 bg-green-50 border-green-200 print:hidden" data-testid="email-success-alert">
            <AlertDescription className="text-green-800 text-sm sm:text-base">
              ✅ Email envoyé avec succès à {certificate.volunteer_email} !
            </AlertDescription>
          </Alert>
        )}

        {/* Validation Email Success Alert */}
        {validationEmailSuccess && (
          <Alert className="mb-4 sm:mb-6 bg-green-50 border-green-200 print:hidden" data-testid="validation-email-success-alert">
            <AlertDescription className="text-green-800 text-sm sm:text-base">
              ✅ Demande de validation envoyée avec succès à {certificate.signatory_email} !
            </AlertDescription>
          </Alert>
        )}
        
        {/* Status Alert - Hidden when printing */}
        {certificate.status === 'pending_validation' && (
          <Alert className="mb-4 sm:mb-6 bg-yellow-50 border-yellow-200 print:hidden" data-testid="pending-alert">
            <AlertDescription className="text-yellow-800 text-sm sm:text-base">
              ⌛ Cette attestation est en attente de validation. Partagez le lien de validation avec le responsable de l'association.
            </AlertDescription>
          </Alert>
        )}
        
        {certificate.status === 'validated' && (
          <Alert className="mb-4 sm:mb-6 bg-green-50 border-green-200 print:hidden" data-testid="validated-alert">
            <AlertDescription className="text-green-800 text-sm sm:text-base">
              ✅ Cette attestation a été validée le {new Date(certificate.validation_date).toLocaleDateString('fr-FR')}
            </AlertDescription>
          </Alert>
        )}

        {/* Certificate */}
        <Card className="print:shadow-none print:border-0" ref={printRef} data-testid="certificate-detail">
          <CardContent className="p-6 sm:p-12 print:p-8">
            {/* Header with Logo */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 sm:mb-8">
              <div className="flex items-center gap-4">
                {/* Logo SVG intégré */}
                <svg width="60" height="60" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="48" fill="white" stroke="#003DA5" strokeWidth="2"/>
                  <circle cx="28" cy="28" r="10" fill="#003DA5"/>
                  <path d="M18 42C18 38 22 35 28 35C34 35 38 38 38 42V58C38 60 36 62 34 62H22C20 62 18 60 18 58V42Z" fill="#003DA5"/>
                  <circle cx="72" cy="28" r="10" fill="#E30613"/>
                  <path d="M62 42C62 38 66 35 72 35C78 35 82 38 82 42V58C82 60 80 62 78 62H66C64 62 62 60 62 58V42Z" fill="#E30613"/>
                  <path d="M38 50C38 50 42 52 46 52" stroke="#003DA5" strokeWidth="6" strokeLinecap="round"/>
                  <path d="M62 50C62 50 58 52 54 52" stroke="#E30613" strokeWidth="6" strokeLinecap="round"/>
                  <ellipse cx="50" cy="52" rx="8" ry="6" fill="#003DA5"/>
                  <ellipse cx="50" cy="52" rx="6" ry="4" fill="#E30613"/>
                  <ellipse cx="50" cy="52" rx="4" ry="2.5" fill="white"/>
                  <circle cx="50" cy="78" r="12" fill="#003DA5"/>
                  <path d="M44 78L48 82L57 73" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#003DA5' }}>Bénévol'Check</h2>
                  <p className="text-sm sm:text-base font-medium" style={{ color: '#E30613' }}>Attestation de compétences</p>
                </div>
              </div>
            </div>

            <div className="border-t-2 border-indigo-200 pt-4 sm:pt-6 space-y-4 sm:space-y-6">
              {/* Association Info */}
              <div className="text-sm sm:text-base">
                <p className="text-base sm:text-lg">
                  <strong>L'association :</strong> {certificate.association_name}
                </p>
                <p className="text-base sm:text-lg">
                  <strong>Représentée par :</strong> {certificate.signatory_name}
                </p>
              </div>

              {/* Main Statement */}
              <div className="bg-indigo-50 p-4 sm:p-6 rounded-lg text-center my-4 sm:my-6">
                <p className="text-lg sm:text-xl font-semibold mb-2">Témoigne que</p>
                <p className="text-2xl sm:text-3xl font-bold text-indigo-900 my-3 sm:my-4" data-testid="certificate-volunteer">
                  {certificate.volunteer_name}
                </p>
                <p className="text-base sm:text-lg">A été bénévole dans notre association</p>
              </div>

              {/* Dates and Hours */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-center">
                <div>
                  <p className="text-sm sm:text-base text-gray-600">Période</p>
                  <p className="text-base sm:text-lg font-semibold" data-testid="certificate-period">
                    Du {new Date(certificate.start_date).toLocaleDateString('fr-FR')} au {new Date(certificate.end_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <p className="text-sm sm:text-base text-gray-600">Volume horaire</p>
                  <p className="text-base sm:text-lg font-semibold text-indigo-600" data-testid="certificate-volume">
                    {certificate.hours_volume} heures
                  </p>
                </div>
              </div>

              {/* Mission */}
              <div className="text-sm sm:text-base">
                <p className="text-base sm:text-lg">
                  <strong>Pour l'activité :</strong> {certificate.mission_description}
                </p>
              </div>

              {/* Competencies */}
              {selectedCompetencies.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-semibold mb-4 text-indigo-900">
                    Au cours de cette mission, il/elle a fait preuve de :
                  </h3>

                  {/* Dans l'action */}
                  {selectedCompetencies.some(c => categorizedCompetencies.action.includes(c)) && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-lg mb-3 text-indigo-700">Dans l'action</h4>
                      <ul className="space-y-2 ml-4">
                        {categorizedCompetencies.action
                          .filter(key => certificate.competencies[key])
                          .map(key => (
                            <li key={key} className="flex items-start">
                              <span className="mr-2">•</span>
                              <div>
                                <strong>{competenciesLabels[key].label} :</strong>{' '}
                                {competenciesLabels[key].desc}
                              </div>
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}

                  {/* Dans l'équipe */}
                  {selectedCompetencies.some(c => categorizedCompetencies.team.includes(c)) && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-lg mb-3 text-indigo-700">Dans l'équipe</h4>
                      <ul className="space-y-2 ml-4">
                        {categorizedCompetencies.team
                          .filter(key => certificate.competencies[key])
                          .map(key => (
                            <li key={key} className="flex items-start">
                              <span className="mr-2">•</span>
                              <div>
                                <strong>{competenciesLabels[key].label} :</strong>{' '}
                                {competenciesLabels[key].desc}
                              </div>
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}

                  {/* Dans l'association */}
                  {selectedCompetencies.some(c => categorizedCompetencies.association.includes(c)) && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-lg mb-3 text-indigo-700">Dans l'association</h4>
                      <ul className="space-y-2 ml-4">
                        {categorizedCompetencies.association
                          .filter(key => certificate.competencies[key])
                          .map(key => (
                            <li key={key} className="flex items-start">
                              <span className="mr-2">•</span>
                              <div>
                                <strong>{competenciesLabels[key].label} :</strong>{' '}
                                {competenciesLabels[key].desc}
                              </div>
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Comment from validator */}
              {certificate.comment && certificate.status === 'validated' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">💬 Commentaire du responsable</h3>
                  <p className="text-blue-800">{certificate.comment}</p>
                </div>
              )}

              {/* Panneau de validation pour les admins */}
              {certificate.status === 'pending_validation' && user?.role === 'association_admin' && (
                <Card className="mt-8 border-2 border-yellow-400 bg-yellow-50 print:hidden" data-testid="validation-panel">
                  <CardHeader>
                    <CardTitle className="text-yellow-800 flex items-center gap-2">
                      ⚠️ Attestation en attente de validation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {validationSuccess ? (
                      <Alert className="bg-green-100 border-green-400">
                        <AlertDescription className="text-green-800">
                          ✅ Attestation validée avec succès ! Un email de confirmation a été envoyé au bénévole.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        {error && (
                          <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                          </Alert>
                        )}

                        {/* Modification des compétences */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">Modifier les compétences</h3>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setEditMode(!editMode)}
                              data-testid="toggle-edit-mode"
                            >
                              {editMode ? '✕ Annuler' : '✏️ Modifier'}
                            </Button>
                          </div>

                          {editMode && (
                            <div className="space-y-6 p-4 bg-white rounded-lg border">
                              {competencyCategories.map((category, idx) => (
                                <div key={idx}>
                                  <h4 className="font-semibold text-indigo-700 mb-3">{category.title}</h4>
                                  <div className="space-y-2">
                                    {category.items.map((item) => (
                                      <label key={item.key} className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                        <input
                                          type="checkbox"
                                          checked={competencies[item.key] || false}
                                          onChange={() => handleCompetencyChange(item.key)}
                                          className="mt-1 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                          data-testid={`competency-${item.key}`}
                                        />
                                        <div className="text-sm">
                                          <span className="font-medium">{item.label}</span>
                                          <span className="text-gray-600"> : {item.desc}</span>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Commentaire */}
                        <div>
                          <Label htmlFor="comment" className="font-semibold text-lg mb-2 block">
                            Ajouter un commentaire (optionnel)
                          </Label>
                          <Textarea
                            id="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Ajoutez vos observations sur le bénévole..."
                            rows={3}
                            className="bg-white"
                            data-testid="validation-comment"
                          />
                        </div>

                        {/* Bouton de validation */}
                        <Button
                          onClick={handleValidate}
                          disabled={validating}
                          className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                          data-testid="validate-certificate-btn"
                        >
                          {validating ? '⏳ Validation en cours...' : '✅ Valider cette attestation'}
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Signature */}
              {certificate.status === 'validated' && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="font-semibold">Fait à {certificate.signing_city || certificate.association_name}</p>
                      <p>Le {new Date(certificate.validation_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold mb-12">Signature</p>
                      <p>{certificate.signatory_name}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Composant dédié à l'impression - invisible à l'écran */}
      {certificate && <PrintableCertificate certificate={certificate} />}
    </div>
  );
};

export default CertificateDetailPage;